// src/firebase.js
import { initializeApp, getApps, getApp } from 'firebase/app';
import { initializeFirestore, memoryLocalCache, connectFirestoreEmulator } from 'firebase/firestore';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaEnterpriseProvider, ReCaptchaV3Provider } from 'firebase/app-check';

// Use environment variables OR fallback to hardcoded (temporary fix)
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || 'AIzaSyDUmdT_zy_b_yMSknVPnP4_8i9qPdBO4Jo',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || 'stema-office-systems.firebaseapp.com',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || 'stema-office-systems',
  // Use the correct default storage bucket for the project
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'stema-office-systems.firebasestorage.app',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '538716812717',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '1:538716812717:web:9f7e9b4c15cb8a0b7add24'
};

// Prevent reinitialization
const app = !getApps().length ? initializeApp(firebaseConfig) : getApp();

// ===================================================================
// APP CHECK ENABLED (reCAPTCHA v3)
// -------------------------------------------------------------------
// - Authentication: SHOULD be set to "Monitoring" in Firebase Console
//   so phone/email login are not blocked by App Check.
// - Firestore & Storage: SHOULD be set to "Enforced" so all data access
//   still requires a valid App Check token.
// ===================================================================

let appCheck = null;

// TEMPORARILY DISABLED FOR DEBUGGING
// App Check is causing CORS-like errors during development
// TODO: Re-enable after registering debug tokens in Firebase Console
/*
if (typeof window !== 'undefined') {
  try {
    const isLocalhost = window.location.hostname === 'localhost';

    // On localhost, use an App Check DEBUG token so Firestore/Storage work even when enforced.
    if (isLocalhost) {
      window.FIREBASE_APPCHECK_DEBUG_TOKEN =
        process.env.REACT_APP_APPCHECK_DEBUG_TOKEN || true;
      console.log('🧪 App Check debug token enabled for localhost');
    }

    const enterpriseKey = process.env.REACT_APP_RECAPTCHA_ENTERPRISE_KEY;
    const v3Key =
      process.env.REACT_APP_RECAPTCHA_V3_KEY ||
      '6LeyFR8sAAAAAM_9NLCVRH7KYejNT842ovIK6PvQ';

    const provider = enterpriseKey
      ? new ReCaptchaEnterpriseProvider(enterpriseKey)
      : new ReCaptchaV3Provider(v3Key);

    appCheck = initializeAppCheck(app, {
      provider,
      isTokenAutoRefreshEnabled: true
    });

    console.log(
      `✅ App Check INITIALIZED (${enterpriseKey ? 'Enterprise' : 'reCAPTCHA v3'})`
    );
  } catch (error) {
    console.error('Failed to initialize App Check:', error);
    appCheck = null;
  }
}
*/

console.log('⚠️ App Check DISABLED - uploads run without token enforcement');

// ── Startup diagnostics — helps distinguish localhost vs production issues ──
if (typeof window !== 'undefined') {
  const env = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? '🏠 localhost' : `🌐 production (${window.location.hostname})`;
  console.group(`[⏱ perf] Firebase startup — ${env}`);
  console.log('Project ID    :', firebaseConfig.projectId);
  console.log('Storage bucket:', firebaseConfig.storageBucket);
  console.log('Auth domain   :', firebaseConfig.authDomain);
  console.log(
    'Functions region: asia-southeast1 (Singapore) — ' +
    'Storage bucket region is separate; check Firebase Console → Storage → Rules → Bucket location'
  );
  if (firebaseConfig.storageBucket?.endsWith('.appspot.com')) {
    console.warn('⚠️  Storage bucket uses legacy .appspot.com domain — likely US-Central1.');
    console.warn('   Uploads from Singapore may be 200–500 ms slower per file than a regional bucket.');
  }
  console.groupEnd();
}

// Memory cache: avoids IndexedDB entirely, preventing the Firestore internal
// assertion errors (ID: b815 / ca9) that occur with persistent caches in
// development when multiple tabs are open or the IndexedDB state gets stale.
const db = initializeFirestore(app, {
  localCache: memoryLocalCache()
});
const auth = getAuth(app);
const storage = getStorage(app);
const functions = getFunctions(app, 'asia-southeast1');

// Opt-in ONLY — REACT_APP_USE_FIRESTORE_EMULATOR must be explicitly set to
// 'true' (e.g. by the Playwright emulator dry-run harness). Never touched by
// a normal `npm start`/production build, so this can't accidentally point
// the real app at a local emulator.
if (typeof window !== 'undefined' && process.env.REACT_APP_USE_FIRESTORE_EMULATOR === 'true') {
  connectFirestoreEmulator(db, '127.0.0.1', 8180);
  connectAuthEmulator(auth, 'http://127.0.0.1:9199', { disableWarnings: true });
  // Functions too, or a callable invoked during an emulator run would reach
  // PRODUCTION while every surrounding read hit the emulator — the worst
  // possible split, since the call would look local and act live.
  connectFunctionsEmulator(functions, '127.0.0.1', 5101);
  // Storage too, for the same reason — and one more: an emulator run that
  // uploaded to the real bucket would leave real files behind AND fire the real
  // convertUploadedMedia function against production data. See
  // functions/mediaConvert.js.
  connectStorageEmulator(storage, '127.0.0.1', 9299);
  console.log('🧪 Connected to Firestore/Auth/Functions/Storage EMULATORS (REACT_APP_USE_FIRESTORE_EMULATOR=true)');
}

// Helper function to get user role from Firebase token (Custom Claims)
export const getUserRole = async () => {
  const user = auth.currentUser;
  if (!user) return null;
  
  try {
    const token = await user.getIdTokenResult();
    return token.claims.role || 'worker';
  } catch (error) {
    console.error('Error getting user role:', error);
    return 'worker';
  }
};

// Helper function to refresh user token (call this after role changes)
export const refreshUserToken = async () => {
  const user = auth.currentUser;
  if (user) {
    await user.getIdToken(true);
  }
};

export { app, db, auth, storage, functions, appCheck, firebaseConfig };
